// The organisation's tax categories, under Money → Tax and invoices (F-PAY-11; spec/06 §10.1, spec/13 §13), in the
// kit's collection pattern (`ListCard`, `ListRow`, 2026-10-07): one row per category — its name, then "18% · SAC
// 998439" — with Edit; adding or editing one is a small centred dialog (name, HSN/SAC code, GST rate), where it is also
// archived, after saying what follows.
//
//  - Anyone who can open settings reads them; adding, changing or archiving one is offered only when
//    `GET /v1/me/access` says the member holds `tax:manage` (the server's `403` stays the enforcement).
//  - The India defaults are seeded by the server the first time the list is read; they are the accountant's to
//    confirm and edit. A rate change applies to what is sold from then on and never re-prices a placed sale.
//  - A category is never deleted: archiving takes it off the pickers, and what was sold under it keeps its code.

import type { TaxCategory } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Badge, Button, Dialog, ListCard, ListRow, Skeleton, TextField, useToast } from '@clavian/ui-web';
import { useState, type FormEvent, type ReactNode } from 'react';

import { PROBLEM, problemCode } from '../api/problems';
import { useProblem } from '../api/use-problem';
import { useAccess } from '../api/queries';
import { LoadFailure } from '../shell/LoadFailure';
import { Notice, Section } from '../ui';
import { bpsOf, percentOf, useCreateTaxCategory, useTaxCategories, useUpdateTaxCategory } from './tax-categories';
import { TAX_MANAGE } from './tax-registration';

export function TaxCategoriesGroup({ organizationId }: { readonly organizationId: string }): ReactNode {
  const { t } = useTranslation('console');
  const access = useAccess({ organizationId });
  const categories = useTaxCategories(organizationId);
  const canManage = access.status === 'ready' && access.can(TAX_MANAGE);
  const [editing, setEditing] = useState<{ readonly category: TaxCategory | null } | null>(null);

  if (categories.data === undefined) {
    return (
      <Section title={t('taxCategories.title')} description={t('taxCategories.hint')}>
        {categories.isError && problemCode(categories.error) === PROBLEM.forbidden ? (
          <Notice role="note">{t('taxCategories.noAccess')}</Notice>
        ) : categories.isError ? (
          <LoadFailure error={categories.error} message={t('taxCategories.unavailable')} onRetry={() => void categories.refetch()} />
        ) : (
          <Skeleton shape="text" lines={4} />
        )}
      </Section>
    );
  }

  const list = categories.data;
  const active = list.filter((category) => !category.archived).length;
  const addButton = canManage ? (
    <Button variant="secondary" size="sm" onClick={() => setEditing({ category: null })}>
      {t('taxCategories.add')}
    </Button>
  ) : undefined;

  return (
    <>
      <ListCard
        title={t('taxCategories.title')}
        summary={t('taxCategories.summary', { count: active })}
        count={list.length}
        listLabel={t('taxCategories.title')}
        emptyText={t('taxCategories.hint')}
        {...(addButton === undefined ? {} : { action: addButton })}
        footer={
          <div className="settings-tax-categories__footer">
            <p className="console-muted">{t('taxCategories.hint')}</p>
            <p className="console-muted">{t('taxCategories.passesNote')}</p>
            {access.status === 'ready' && !canManage ? <Notice role="note">{t('taxCategories.readOnly')}</Notice> : null}
          </div>
        }
      >
        {list.map((category) => (
          <ListRow
            key={category.id}
            title={category.name}
            meta={[t('taxCategories.rateValue', { rate: percentOf(category.rateBps) }), category.hsnSac === null ? t('taxCategories.noCode') : t('taxCategories.codeValue', { code: category.hsnSac })].join(' · ')}
            muted={category.archived}
            badge={
              category.archived || category.taxPoint === 'redemption' ? (
                <>
                  {category.archived ? <Badge tone="neutral">{t('taxCategories.archived')}</Badge> : null}
                  {category.taxPoint === 'redemption' ? <Badge tone="info">{t('taxCategories.atRedemption')}</Badge> : null}
                </>
              ) : undefined
            }
            {...(canManage
              ? {
                  actions: (
                    <Button variant="ghost" size="sm" onClick={() => setEditing({ category })} aria-label={t('taxCategories.editNamed', { name: category.name })}>
                      {t('taxCategories.edit')}
                    </Button>
                  ),
                }
              : {})}
          />
        ))}
      </ListCard>
      <Dialog
        open={editing !== null}
        onOpenChange={(open) => (open ? undefined : setEditing(null))}
        size="sm"
        dirty="auto"
        title={editing?.category == null ? t('taxCategories.add') : t('taxCategories.editNamed', { name: editing.category.name })}
        description={t('taxCategories.rateHint')}
      >
        {editing === null ? null : <CategoryForm organizationId={organizationId} category={editing.category ?? undefined} onDone={() => setEditing(null)} />}
      </Dialog>
    </>
  );
}

function CategoryForm({ organizationId, category, onDone }: { readonly organizationId: string; readonly category?: TaxCategory | undefined; readonly onDone: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const toast = useToast();
  const create = useCreateTaxCategory(organizationId);
  const update = useUpdateTaxCategory(organizationId);
  const [name, setName] = useState(category?.name ?? '');
  const [code, setCode] = useState(category?.hsnSac ?? '');
  const [rate, setRate] = useState(category === undefined ? '18' : percentOf(category.rateBps));
  const [errors, setErrors] = useState<{ name?: string; code?: string; rate?: string; form?: string }>({});
  // Archiving matters — products taxed under it keep it — so it asks first, in place.
  const [confirming, setConfirming] = useState(false);

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const rateBps = bpsOf(rate);
    const trimmedCode = code.trim();
    const next: typeof errors = {};
    if (name.trim() === '') next.name = t('taxCategories.nameRequired');
    if (trimmedCode !== '' && !/^\d{4,8}$/.test(trimmedCode)) next.code = t('taxCategories.codeInvalid');
    if (rateBps === null) next.rate = t('taxCategories.rateInvalid');
    setErrors(next);
    if (Object.keys(next).length > 0 || rateBps === null) return;
    const onError = (error: unknown) => {
      const nameMessage = problem.fieldMessage(error, 'name');
      if (problemCode(error) === PROBLEM.conflict) setErrors({ form: t('taxCategories.nameTaken') });
      else if (nameMessage !== undefined) setErrors({ name: nameMessage });
      else setErrors({ form: problem.text(error, { fallback: t('taxCategories.refused') }) });
    };
    const onSuccess = () => {
      toast.show({ tone: 'ok', title: t('taxCategories.saved') });
      onDone();
    };
    if (category === undefined) {
      create.mutate({ name: name.trim(), rateBps, ...(trimmedCode === '' ? {} : { hsnSac: trimmedCode }) }, { onSuccess, onError });
    } else {
      update.mutate(
        { taxCategoryId: category.id, body: { name: name.trim(), rateBps, hsnSac: trimmedCode === '' ? null : trimmedCode } },
        { onSuccess, onError },
      );
    }
  }

  return (
    <form className="console-form settings-tax-categories__form" aria-label={category === undefined ? t('taxCategories.add') : t('taxCategories.editNamed', { name: category.name })} noValidate onSubmit={submit}>
      {errors.form !== undefined ? (
        <Notice tone="danger" role="alert">
          {errors.form}
        </Notice>
      ) : null}
      <TextField label={t('taxCategories.name')} required value={name} onChange={(event) => setName(event.target.value)} error={errors.name} />
      <TextField label={t('taxCategories.code')} showOptional inputMode="numeric" value={code} onChange={(event) => setCode(event.target.value)} description={t('taxCategories.codeHint')} error={errors.code} />
      <TextField label={t('taxCategories.rate')} required numeric inputMode="decimal" value={rate} onChange={(event) => setRate(event.target.value)} error={errors.rate} />
      {confirming && category !== undefined ? (
        <Notice tone="warn" role="alert" title={t('taxCategories.archiveTitle', { name: category.name })}>
          <p>{t('taxCategories.archiveBody')}</p>
          <div className="console-form__actions">
            <Button type="button" variant="ghost" size="sm" onClick={() => setConfirming(false)}>
              {t('taxCategories.keep')}
            </Button>
            <Button
              type="button"
              variant="danger"
              size="sm"
              busy={update.isPending}
              onClick={() => update.mutate({ taxCategoryId: category.id, body: { archived: true } }, { onSuccess: onDone })}
            >
              {t('taxCategories.archive')}
            </Button>
          </div>
        </Notice>
      ) : null}
      <div className="console-form__actions console-form__actions--end">
        {category !== undefined ? (
          category.archived ? (
            <Button type="button" variant="secondary" busy={update.isPending} onClick={() => update.mutate({ taxCategoryId: category.id, body: { archived: false } }, { onSuccess: onDone })}>
              {t('taxCategories.restore')}
            </Button>
          ) : (
            <Button type="button" variant="ghost" disabled={confirming} onClick={() => setConfirming(true)}>
              {t('taxCategories.archive')}
            </Button>
          )
        ) : null}
        <Button type="button" variant="ghost" onClick={onDone}>
          {t('taxCategories.cancel')}
        </Button>
        <Button type="submit" variant="primary" busy={create.isPending || update.isPending}>
          {t('taxCategories.save')}
        </Button>
      </div>
    </form>
  );
}
